import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ELIGIBLE_PROGRAMS,
  NLAD_EXCEPTION_CODES,
  SUBSIDY_ADDRESS_TYPES,
  SUBSIDY_ENROLLMENT_TYPES,
  accountTypeChangeSchema,
} from '@app/shared';
import type { AccountTypeChangeInput, SubsidyEnrollmentType } from '@app/shared';
import {
  CheckboxInput,
  SelectInput,
  TextInput,
  applyServerErrors,
  toOptions,
} from '../../components/form';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { formatCents } from '../../lib/format';
import { RemoveLifelineSection } from '../lifeline/RemoveLifeline';
import { useCustomerContext } from './CustomerLayout';
import { useAccountTypeScreen, useAddProduct, useChangeAccountType } from './api';
import { Section, customerPath } from './parts';

const EMPTY: AccountTypeChangeInput = {
  addressType: '',
  ssnLast4: '',
  dob: '',
  enrollmentType: '',
  planId: '',
  eligiblePrograms: [],
  incomeBased: false,
  householdSize: '',
  bypassNlad: false,
  nladExceptionCode: '',
};

const grid = { display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } };

/**
 * Change Account Type (PC648), the reference's "CSR Screen For Change Account Type": moves an
 * Active prepaid customer into ACP or Lifeline. Name, address and phone are shown read-only;
 * the eligibility is checked with NLAD unless "Click to Bypass NLAD" is used with an exception
 * code. An account that is not Active shows only the reference's message.
 */
export function ChangeAccountTypePage() {
  const { customer } = useCustomerContext();
  const notify = useNotify();
  const navigate = useNavigate();
  const screen = useAccountTypeScreen(customer.id);
  const change = useChangeAccountType(customer.id);
  const { control, handleSubmit, setError, setValue, watch } = useForm<AccountTypeChangeInput>({
    resolver: zodResolver(accountTypeChangeSchema),
    defaultValues: EMPTY,
  });
  const enrollmentType = watch('enrollmentType') as SubsidyEnrollmentType | '';
  const incomeBased = watch('incomeBased');
  const bypassNlad = watch('bypassNlad');

  // The plan list belongs to the enrollment type: a new type starts with no plan chosen.
  useEffect(() => setValue('planId', ''), [enrollmentType, setValue]);

  if (screen.isError)
    return <ErrorState error={screen.error} onRetry={() => void screen.refetch()} />;
  if (!screen.data) return <LoadingState label="Loading…" />;
  const data = screen.data;

  const submit = handleSubmit(async (values) => {
    try {
      const result = await change.mutateAsync(values);
      notify.success(result.message);
      navigate(customerPath(customer.id));
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  const plans = enrollmentType ? data.plans[enrollmentType] : [];
  return (
    <Section title="Change Account Type (PC648)">
      <RemoveLifelineSection customerId={customer.id} accountType={customer.line.accountType} />
      <Typography variant="h3" sx={{ mb: 1 }}>
        CSR Screen For Change Account Type
      </Typography>
      {data.blocked ? (
        <Alert severity="info">{data.blocked}</Alert>
      ) : (
        <Box
          component="form"
          noValidate
          aria-label="Change Account Type"
          onSubmit={(e) => void submit(e)}
          sx={{ display: 'grid', gap: 2, maxWidth: 900 }}
        >
          <Box sx={grid}>
            <TextField label="First Name" value={data.firstName} disabled />
            <TextField label="Last Name" value={data.lastName} disabled />
            <TextField label="Address" value={data.address} disabled />
            <SelectInput
              control={control}
              name="addressType"
              label="Address Type"
              required
              options={toOptions(SUBSIDY_ADDRESS_TYPES)}
            />
            <TextField label="Phone Number" value={data.phone} disabled />
            <TextInput
              control={control}
              name="ssnLast4"
              label="SSN (Last 4 Digits)"
              required
              inputMode="numeric"
              slotProps={{ htmlInput: { maxLength: 4 } }}
            />
            <TextInput
              control={control}
              name="dob"
              label="DOB(MM-DD-YYYY)"
              required
              placeholder="MM-DD-YYYY"
              slotProps={{ htmlInput: { maxLength: 10 } }}
            />
            <SelectInput
              control={control}
              name="enrollmentType"
              label="Enrollment Type"
              required
              options={toOptions(SUBSIDY_ENROLLMENT_TYPES)}
            />
            <SelectInput
              control={control}
              name="planId"
              label="Plan"
              required
              disabled={!enrollmentType}
              helperText={
                enrollmentType && plans.length === 0
                  ? `No ${enrollmentType} plan is offered for this line.`
                  : undefined
              }
              options={plans.map((p) => ({
                value: p.id,
                label: `${p.name} (${formatCents(p.priceCents)})`,
              }))}
            />
          </Box>

          <Controller
            control={control}
            name="eligiblePrograms"
            render={({ field, fieldState }) => {
              const chosen = field.value ?? [];
              return (
                <FormControl component="fieldset" error={Boolean(fieldState.error)}>
                  <FormLabel component="legend">Eligible Program</FormLabel>
                  <FormGroup>
                    {ELIGIBLE_PROGRAMS.map((program) => (
                      <FormControlLabel
                        key={program}
                        label={program}
                        control={
                          <Checkbox
                            size="small"
                            checked={chosen.includes(program)}
                            onChange={(e) =>
                              field.onChange(
                                e.target.checked
                                  ? [...chosen, program]
                                  : chosen.filter((p) => p !== program),
                              )
                            }
                          />
                        }
                      />
                    ))}
                  </FormGroup>
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              );
            }}
          />

          <Box>
            <FormLabel component="legend">Income Based</FormLabel>
            {/* Our wording of the reference's checkbox (its exact text was not recorded). */}
            <CheckboxInput
              control={control}
              name="incomeBased"
              label="Household income is at or below 200% of the federal poverty guidelines"
            />
            {incomeBased ? (
              <TextInput
                control={control}
                name="householdSize"
                label="How many people are in your household?"
                required
                inputMode="numeric"
                slotProps={{ htmlInput: { maxLength: 3 } }}
                sx={{ display: 'block', mt: 1 }}
              />
            ) : null}
          </Box>

          <Box>
            <FormLabel component="legend">Bypass NLAD?</FormLabel>
            <CheckboxInput control={control} name="bypassNlad" label="Click to Bypass NLAD" />
            {bypassNlad ? (
              <Box sx={{ mt: 1, maxWidth: 420 }}>
                <SelectInput
                  control={control}
                  name="nladExceptionCode"
                  label="NLAD Exception Code"
                  required
                  options={toOptions(NLAD_EXCEPTION_CODES)}
                />
              </Box>
            ) : null}
          </Box>

          <Box>
            <Button type="submit" variant="contained" disabled={change.isPending}>
              Submit
            </Button>
          </Box>
        </Box>
      )}
    </Section>
  );
}

/**
 * Add Product (PC945), the reference's "CSR Screen For Add Product": the account's Customer ID,
 * Phone Number and Plan (not editable) and Submit, which sends the request to the carrier and
 * shows its answer in green on success, in red otherwise. There is no product picker.
 */
export function AddProductPage() {
  const { customer } = useCustomerContext();
  const add = useAddProduct(customer.id);
  return (
    <Section title="Add Product (PC945)">
      <Typography variant="h3" sx={{ mb: 1 }}>
        CSR Screen For Add Product
      </Typography>
      <Box
        component="form"
        noValidate
        aria-label="Add Product"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
        sx={{ display: 'grid', gap: 1.5, maxWidth: 420 }}
      >
        <TextField label="Customer ID" value={customer.customerId} disabled />
        <TextField label="Phone Number" value={customer.line.mdn ?? ''} disabled />
        <TextField label="Plan" value={customer.line.plan?.name ?? ''} disabled />
        <Box>
          <Button type="submit" variant="contained" disabled={add.isPending}>
            Submit
          </Button>
        </Box>
        {add.isSuccess ? <Alert severity="success">{add.data.message}</Alert> : null}
        {add.isError ? (
          <Alert severity="error">
            {add.error instanceof Error ? add.error.message : 'The request failed.'}
          </Alert>
        ) : null}
      </Box>
    </Section>
  );
}
